import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";

import type { CommunitySupportProjectDto } from "@/api/types";
import { SegmentGroup } from "@/design-system/SegmentGroup";
import { Skeleton } from "@/design-system/Skeleton";
import { Stack } from "@/design-system/Stack";
import { MAX_ANALYTICS_PERCENTAGE_VALUE } from "@/helpers/analytics";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { usePortfolioAnalyticsQueries } from "@/queries/portfolio-analytics/queryOptions";
import { useTranslation } from "@/translations";

import { RingChart } from "../Charts/RingChart";
import { Users01Icon } from "../Icons/Users01Icon";
import { AnalyticsWidgetCard } from "./AnalyticsWidgetCard";
import { AnalyticsWidgetProjectList } from "./AnalyticsWidgetProjectList";
import {
  derivePrimaryDateRangeFromPeriod,
  deriveSecondaryDateRangeFromPeriod,
  formatAnalyticsDatePayload,
  formatAnalyticsPercentageValue,
} from "./helpers";
import type { AnalyticsWidgetBaseProps } from "./types";
import { useMetricHelp } from "./useMetricHelp";

type CommunitySupportWidgetProps = AnalyticsWidgetBaseProps;

export function CommunitySupportWidget({ projectIds = [], period }: CommunitySupportWidgetProps): React.ReactNode {
  const [selectedProjectSegment, setSelectedProjectSegment] = useState<"positive" | "negative">("positive");

  const { t } = useTranslation();
  const help = useMetricHelp();
  const portfolioAnalyticsQueries = usePortfolioAnalyticsQueries();

  const secondaryDates = deriveSecondaryDateRangeFromPeriod(period);
  const { data: secondaryData } = useQuery(
    portfolioAnalyticsQueries.communitySupport({
      projectId: projectIds.length === 1 ? projectIds[0] : undefined,
      startDate: formatAnalyticsDatePayload(secondaryDates.startDate),
      endDate: formatAnalyticsDatePayload(secondaryDates.endDate),
    }),
  );

  const primaryDates = derivePrimaryDateRangeFromPeriod(period);
  const { data: primaryData, isPending } = useQuery({
    ...portfolioAnalyticsQueries.communitySupport({
      projectId: projectIds.length === 1 ? projectIds[0] : undefined,
      startDate: primaryDates.startDate ? formatAnalyticsDatePayload(primaryDates.startDate) : undefined,
      endDate: primaryDates.endDate ? formatAnalyticsDatePayload(primaryDates.endDate) : undefined,
    }),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });

  const aggregateData = (projects: CommunitySupportProjectDto[]) => {
    return (projects ?? []).reduce(
      (acc, project) => {
        if (project.helpRequestCount) {
          acc.totalHelpRequests += project.helpRequestCount;
          acc.solvedHelpRequests += project.solvedHelpRequestCount;
        }

        return acc;
      },
      { totalHelpRequests: 0, solvedHelpRequests: 0 },
    );
  };

  const computeScore = (amountSolved: number, amountTotal: number) => {
    return amountTotal > 0 ? (amountSolved / amountTotal) * 100 : 0;
  };

  const primaryProjects = primaryData?.projects ?? [];
  const primaryAggregatedData = aggregateData(primaryProjects);
  const primaryScore = computeScore(primaryAggregatedData.solvedHelpRequests, primaryAggregatedData.totalHelpRequests);

  return (
    <AnalyticsWidgetCard
      title={t("component.analytics-widget.community-support.title")}
      titleTooltip={help("component.analytics-widget.community-support.tooltip")}
      icon={<Users01Icon />}
      isLoading={isPending}
      renderSkeleton={() => <WidgetSkeleton />}
    >
      <section className="flex flex-1 flex-col items-center justify-center gap-space-md text-center">
        <RingChart
          variant="half"
          value={primaryScore}
          max={MAX_ANALYTICS_PERCENTAGE_VALUE}
          renderValue={formatAnalyticsPercentageValue}
        />
        <h4 className="text-heading-4">{t("component.analytics-widget.community-support.subtitle")}</h4>
      </section>
      {primaryData && primaryData.projects.length > 1 && (
        <>
          <SegmentGroup
            value={selectedProjectSegment}
            onChange={(value) => setSelectedProjectSegment(value)}
            items={[
              {
                label: t("component.analytics-widget.project-segment-group.label.top-performing"),
                value: "positive",
              },
              {
                label: t("component.analytics-widget.project-segment-group.label.bottom-performing"),
                value: "negative",
              },
            ]}
          />
          <AnalyticsWidgetProjectList
            scoreFormat="percentage"
            order={selectedProjectSegment === "positive" ? "descending" : "ascending"}
            projects={(primaryData?.projects ?? []).map((currPrimaryProject) => {
              const currSecondaryProject = secondaryData?.projects.find(
                (currProject) => currProject.projectId === currPrimaryProject.projectId,
              );

              return {
                projectId: currPrimaryProject.projectId,
                projectName: currPrimaryProject.projectName,
                projectLogo: currPrimaryProject.projectLogo,
                score: computeScore(currPrimaryProject.solvedHelpRequestCount, currPrimaryProject.helpRequestCount),
                referenceScore: currSecondaryProject
                  ? computeScore(currSecondaryProject.solvedHelpRequestCount, currSecondaryProject.helpRequestCount)
                  : undefined,
              };
            })}
          />
        </>
      )}
    </AnalyticsWidgetCard>
  );
}

function WidgetSkeleton(): React.ReactNode {
  return (
    <Stack gap="md" align="center">
      <Skeleton height="20" width="40" shape="roundedTop" />
      <Skeleton height="6" width="1/2" />
      <Skeleton height="8" />
    </Stack>
  );
}
